// OMNI Design System Theme for Chakra UI v3
// Complete theme configuration mapping OMNI CSS to Chakra tokens

import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { omniColors, legacyBrandColors } from './tokens/colors';
import { omniTypography, omniTextStyles } from './tokens/typography';
import { omniSpacing } from './tokens/spacing';
import { omniShadows } from './tokens/shadows';
import { buttonRecipe } from './recipes/button';
import { inputRecipe } from './recipes/input';
import { selectRecipe } from './recipes/select';
import { tableRecipe } from './recipes/table';
import { cardRecipe } from './recipes/card';
import { modalRecipe } from './recipes/modal';
import {
  omniDropZoneRecipe,
  omniDragItemRecipe,
} from './recipes/omni-drag-drop';
import { omniModalRecipe } from './recipes/omni-modal';
import { omniCloseButtonRecipe } from './recipes/omni-close-button';
import {
  omniSelectableItemRecipe,
  omniSelectionBadgeRecipe,
  omniTabButtonRecipe,
  omniInputRecipe,
} from './recipes/omni-selection';
import { omniNumberInputRecipe } from './recipes/omni-numberinput';
import { omniAppHeaderRecipes } from './recipes/omni-appheader';
import { omniGlobalStyles } from './global';

// OMNI theme configuration
const omniConfig = defineConfig({
  cssVarsPrefix: 'omni',
  cssVarsRoot: ':where(:root, :host)',

  globalCss: omniGlobalStyles,

  theme: {
    // Breakpoints for responsive design
    breakpoints: {
      sm: '30em', // 480px
      md: '48em', // 768px
      lg: '62em', // 992px
      xl: '80em', // 1280px
      '2xl': '96em', // 1536px
    },

    // Design tokens
    tokens: {
      // Colors
      colors: {
        ...omniColors,
        ...legacyBrandColors, // Keep for backward compatibility
      },

      // Typography
      fonts: omniTypography.fonts,
      fontSizes: omniTypography.fontSizes,
      fontWeights: omniTypography.fontWeights,
      lineHeights: omniTypography.lineHeights,
      letterSpacings: omniTypography.letterSpacings,

      // Spacing & Layout
      spacing: omniSpacing.spacing,
      sizes: omniSpacing.sizes,
      radii: omniSpacing.radii,
      borderWidths: omniSpacing.borderWidths,
      zIndex: omniSpacing.zIndex,

      // Animation
      durations: omniSpacing.durations,
      easings: omniSpacing.easings,

      // Shadows & Effects
      shadows: omniShadows.shadows,
      gradients: omniShadows.gradients,
    },

    // Semantic tokens for context-aware theming
    semanticTokens: {
      colors: {
        // Brand colors
        brand: {
          50: { value: '{colors.brand.50}' },
          100: { value: '{colors.brand.100}' },
          200: { value: '{colors.brand.200}' },
          300: { value: '{colors.brand.300}' },
          400: { value: '{colors.brand.400}' },
          500: { value: '{colors.brand.500}' },
          600: { value: '{colors.brand.600}' },
          700: { value: '{colors.brand.700}' },
          800: { value: '{colors.brand.800}' },
          900: { value: '{colors.brand.900}' },
        },

        // Background colors
        bg: {
          canvas: { value: '{colors.gray.100}' }, // Main background
          surface: { value: '{colors.gray.50}' }, // Card/container background
          subtle: { value: '{colors.gray.200}' }, // Subtle background
          muted: { value: '{colors.gray.300}' }, // Muted background
          brand: { value: '{colors.brand.500}' }, // Brand background
          'brand.subtle': { value: '{colors.brand.50}' }, // Subtle brand background
          'brand.muted': { value: '{colors.brand.100}' }, // Muted brand background
          navbar: { value: '{colors.navbar.500}' }, // Navbar background
          'navbar.subtle': { value: '{colors.navbar.100}' }, // Subtle navbar background
          accent: { value: '{colors.brand.600}' }, // Accent background
          interactive: { value: '{colors.brand.500}' }, // Interactive elements
          'interactive.hover': { value: '{colors.brand.600}' }, // Hover state
          'interactive.active': { value: '{colors.brand.700}' }, // Active state
        },

        // Text colors
        fg: {
          default: { value: '{colors.gray.900}' }, // Primary text
          muted: { value: '{colors.gray.700}' }, // Secondary text
          subtle: { value: '{colors.gray.500}' }, // Tertiary text
          placeholder: { value: '{colors.gray.400}' }, // Placeholder text
          inverse: { value: '{colors.white}' }, // Text on dark backgrounds (pure white)
          'on.brand': { value: '{colors.white}' }, // Text on brand backgrounds
          'on.brand.subtle': { value: '{colors.brand.800}' }, // Text on light brand backgrounds
          'brand.emphasis': { value: '{colors.brand.700}' }, // Brand text emphasis
        },

        // Border colors
        border: {
          default: { value: '{colors.gray.200}' }, // Default border
          subtle: { value: '{colors.gray.100}' }, // Subtle border
          muted: { value: '{colors.gray.300}' }, // Muted border
          emphasis: { value: '{colors.brand.500}' }, // Emphasized border
        },

        // Status colors (semantic)
        colorPalette: {
          50: { value: '{colors.colorPalette.50}' },
          100: { value: '{colors.colorPalette.100}' },
          200: { value: '{colors.colorPalette.200}' },
          300: { value: '{colors.colorPalette.300}' },
          400: { value: '{colors.colorPalette.400}' },
          500: { value: '{colors.colorPalette.500}' },
          600: { value: '{colors.colorPalette.600}' },
          700: { value: '{colors.colorPalette.700}' },
          800: { value: '{colors.colorPalette.800}' },
          900: { value: '{colors.colorPalette.900}' },
        },
      },

      shadows: {
        // Component-specific shadows
        button: { value: '{shadows.button-dark}' },
        card: { value: '{shadows.card}' },
        dropdown: { value: '{shadows.dropdown-tooltip}' },
        focus: { value: '{shadows.bulma-focus}' },
      },
    },

    // Text styles for typography patterns
    textStyles: omniTextStyles,

    // Layer styles for common background/border combinations
    layerStyles: {
      card: {
        bg: 'bg.surface',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 'base',
        boxShadow: 'card',
      },

      surface: {
        bg: 'bg.surface',
        border: '1px solid',
        borderColor: 'border.subtle',
        borderRadius: 'base',
      },

      elevated: {
        bg: 'bg.surface',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 'lg',
        boxShadow: 'container-level-two',
      },

      navigation: {
        bg: 'linear-gradient(radial-gradient(circle at 50% 0, navbar.500, navbar.900 89%))',
        color: 'fg.inverse',
      },

      // OMNI-specific layer styles
      'omni.interactive': {
        bg: 'bg.brand',
        color: 'fg.on.brand',
        borderRadius: 'base',
        transition: 'all 200ms ease',
        _hover: {
          bg: 'bg.interactive.hover',
          color: 'fg.on.brand',
        },
      },

      'omni.interactive.subtle': {
        bg: 'bg.brand.subtle',
        color: 'fg.on.brand.subtle',
        borderRadius: 'base',
        border: '1px solid',
        borderColor: 'brand.200',
        transition: 'all 200ms ease',
        _hover: {
          bg: 'bg.brand.muted',
          borderColor: 'brand.300',
          color: 'fg.brand.emphasis',
        },
      },

      'omni.navbar': {
        bg: 'bg.navbar',
        color: 'fg.inverse',
        borderRadius: 'base',
      },

      'omni.dropzone': {
        bg: 'bg.brand.subtle',
        color: 'fg.on.brand.subtle',
        border: '2px dashed',
        borderColor: 'brand.300',
        borderRadius: 'base',
        transition: 'all 200ms ease',
        _hover: {
          bg: 'bg.brand.muted',
          borderColor: 'brand.400',
          color: 'fg.brand.emphasis',
        },
      },

      'omni.dropzone.active': {
        bg: 'bg.brand.muted',
        color: 'fg.brand.emphasis',
        border: '2px solid',
        borderColor: 'brand.500',
        borderRadius: 'base',
        boxShadow: 'focus',
      },
    },

    // Animation styles
    animationStyles: {
      fadeIn: {
        animation: 'fadeIn 200ms ease',
      },

      slideUp: {
        animation: 'slideUp 200ms ease',
      },

      scale: {
        animation: 'scale 150ms ease',
      },

      spin: {
        animation: 'spin 1s linear infinite',
      },
    },

    // Component recipes
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      // OMNI custom recipes
      'omni-drop-zone': omniDropZoneRecipe,
      'omni-drag-item': omniDragItemRecipe,
      'omni-modal': omniModalRecipe,
      'omni-close-button': omniCloseButtonRecipe,
      // OMNI selection recipes
      'omni-selectable-item': omniSelectableItemRecipe,
      'omni-selection-badge': omniSelectionBadgeRecipe,
      'omni-tab-button': omniTabButtonRecipe,
      'omni-input': omniInputRecipe,
      // OMNI AppHeader recipes
      ...omniAppHeaderRecipes,
    },

    // Slot recipes for multi-part components
    slotRecipes: {
      select: selectRecipe,
      table: tableRecipe,
      card: cardRecipe,
      modal: modalRecipe,
      numberInput: omniNumberInputRecipe,
    },
  },
});

// Create the OMNI theme system
export const omniSystem = createSystem(defaultConfig, omniConfig);

// Export for backward compatibility
export const system = omniSystem;
export default omniSystem;

// Export theme configuration for CLI type generation
export { omniConfig };
